<script lang="ts" setup>
import type { AsyncDataRequestStatus, NuxtError } from '#app'

import CategoryFaqs from '~/components/faqs/CategoryFaqs.vue'
import HorizontalMenu from '~/components/menus/HorizontalMenu.vue'
import ConsultationsSection from '~/components/sections/ConsultationsSection.vue'
import MindboxRecommendationsSection from '~/components/sections/MindboxRecommendationsSection.vue'
import { useSharing } from '~/composables/sharing'
import { useWindowHelpers } from '~/composables/window-helpers'
import constants from '~/constants'
import { MindboxOperation } from '~/enums/mindbox-operation'
import PageEnum from '~/enums/page'
import ProductSortEnum from '~/enums/product-sort'
import helpers from '~/helpers'
import JsonApiHelper from '~/helpers/json-api-helper'
import { viewCategory } from '~/services/mindbox-api-service'
import type { BreadcrumbsItem } from '~/types/breadcrumbs'
import type { CategoryData } from '~/types/categories'
import type { Page } from '~/types/json-api'
import type { HorizontalMenuItem } from '~/types/menus'
import type { ProductData } from '~/types/products'

const { $apiHelper } = useNuxtApp()

const route = useRoute()
const router = useRouter()
const { parsePromoCodeFromQuery } = useSharing()
const { scrollTo, scrollToSavedPosition } = useWindowHelpers()

const actualFullPath = ref<string | null>(null)
const isFirstDataLoading = ref(true)
const isProductsDataReloading = ref(false)

// Sort.
const sort = ref(ProductSortEnum.MostPopular)

// Pagination.
const page = ref<Page>({ number: 1, size: 30 })

const bannerImagePaths = computed(() => {
  switch (categoryId.value) {
    case constants.categories.halloween.id:
      return ['/images/banners/28_c.jpg', '/images/banners/28_c_small.jpg']
    case constants.categories.sale1111.id:
      return ['/images/banners/82_c.jpg', '/images/banners/82_c_small.jpg']
    case constants.categories.againstSars.id:
      return ['/images/banners/33_c.jpg', '/images/banners/33_c_small.jpg']
    case constants.categories.newYearMiracle.id:
      return ['/images/banners/35_c.jpg', '/images/banners/35_c_small.jpg']
    case constants.categories.onFebruary23rd.id:
      return ['/images/banners/39_c.jpg', '/images/banners/39_c_small.jpg']
    case constants.categories.consultations.id:
      return ['/images/banners/40_c.jpg', '/images/banners/40_c_small.jpg']
    case constants.categories.womensWeek.id:
      return ['/images/banners/43_c.jpg', '/images/banners/43_c_small.jpg']
    case constants.categories.pancakeWeek.id:
      return ['/images/banners/44_c.jpg', '/images/banners/44_c_small.jpg']
    case constants.categories.halal.id:
      return ['/images/banners/89_c.jpg', '/images/banners/89_c_small.jpg']
    case constants.categories.groceries.id:
    case constants.categories.sweetLife.id:
      return ['/images/banners/48_c.jpg', '/images/banners/48_c_small.jpg']
    case constants.categories.beautyWeek.id:
      return ['/images/banners/54_c.jpg', '/images/banners/54_c_small.jpg']
    case constants.categories.healthWeek.id:
      return ['/images/banners/55_c.jpg', '/images/banners/55_c_small.jpg']
    case constants.categories.detoxWeek.id:
      return ['/images/banners/56_c.jpg', '/images/banners/56_c_small.jpg']
    case constants.categories.sportWeek.id:
      return ['/images/banners/58_c.jpg', '/images/banners/58_c_small.jpg']
    case constants.categories.bestForChildren.id:
      return ['/images/banners/59_c.jpg', '/images/banners/59_c_small.jpg']
    case constants.categories.sunnySummer.id:
      return ['/images/banners/60_c.jpg', '/images/banners/60_c_small.jpg']
    case constants.categories.liquidation.id:
      return ['/images/banners/61_c.jpg', '/images/banners/61_c_small.jpg']
    case constants.categories.forAnimals.id:
      return ['/images/banners/64_c.jpg', '/images/banners/64_c_small.jpg']
    case constants.categories.summerEnergy.id:
      return ['/images/banners/67_c.jpg', '/images/banners/67_c_small.jpg']
    case constants.categories.greenFriday.id:
      return ['/images/banners/80_c.jpg', '/images/banners/80_c_small.jpg']
    case constants.categories.motherDay.id:
      return ['/images/banners/84_c.jpg', '/images/banners/84_c_small.jpg']
    case constants.categories.blackFriday.id:
      return ['/images/banners/85_c.jpg', '/images/banners/85_c_small.jpg']
    default:
      return []
  }
})
const breadcrumbsItems = computed(() => {
  const breadcrumbsItems: BreadcrumbsItem[] = [
    { name: 'Главная', path: PageEnum.Main },
    { name: 'Каталог', path: PageEnum.Search }
  ]
  const parentCategoryData = categoryData.value!.relationships.parentCategory.data

  if (parentCategoryData) {
    breadcrumbsItems.push({
      name: parentCategoryData.attributes.name,
      path: helpers.getCategoryPath(parentCategoryData)
    })
  }

  return breadcrumbsItems
})
const categoryId = computed(() => helpers.getIdFromSlugAndId(route.params.slugAndId as string))
const categoryKey = computed(() => `category-${categoryId.value}`)
const descriptionHtml = computed(() => categoryData.value!.attributes.descriptionHtml)
const menuItems = computed(() =>
  categoryData.value!.relationships.subcategories.data.map(
    (x: CategoryData): HorizontalMenuItem => ({ name: x.attributes.name, path: helpers.getCategoryPath(x) })
  )
)
const name = computed(() => categoryData.value!.attributes.name)
const seoBottomDescriptionHtml = computed(
  // TODO: Rename to "bottomDescriptionHtml"
  () => seoData.value?.bottomDescription ?? null
)
const seoData = computed(() => categoryData.value!.attributes.seoData)
const seoH1 = computed(() => seoData.value?.h1 ?? name.value)

const { data, error, status } = await useLazyAsyncData(
  categoryKey,
  async () => {
    parseQuery()

    try {
      const [categoryResponse, productsResponse] = await Promise.all([
        $apiHelper.categories.getCategory(categoryId.value!),
        $apiHelper.products.getProducts(getProductsParams())
      ])

      const { fullPath, path } = route
      const actualPath = helpers.getCategoryPath(categoryResponse.data)

      if (path !== actualPath) {
        actualFullPath.value = fullPath.replace(path, actualPath)

        if (import.meta.client) {
          history.replaceState({}, '', actualFullPath.value)
        }
      }

      return { categoryResponse, productsResponse }
    } catch (error) {
      showError(error as NuxtError)
    }
  },
  {
    transform: (x) => ({
      categoryResponse: JsonApiHelper.denormalizeResponse(x?.categoryResponse),
      productsResponse: JsonApiHelper.denormalizeResponse(x?.productsResponse)
    })
  }
)

if (error.value) {
  throw createError(error.value as NuxtError)
}

if (actualFullPath.value) {
  await navigateTo(actualFullPath.value, { redirectCode: 301 })
}

const categoryData = computed<CategoryData | null>(() => data.value?.categoryResponse?.data ?? null)
const productsData = computed<ProductData[]>(() => data.value?.productsResponse?.data ?? [])
const totalProducts = computed(() => data.value?.productsResponse?.meta.totalProducts)
const totalPages = computed(
  () => data.value?.productsResponse
    ? helpers.getTotalPages(data.value?.productsResponse.meta.totalProducts, page.value.size)
    : null
)

onBeforeMount(parseQuery)

onMounted(async (): Promise<void> => {
  isFirstDataLoading.value = false

  await parsePromoCodeFromQuery()
})

useHead(() => {
  if (!categoryData.value) {
    return {}
  }

  return {
    title: seoData.value?.title ?? `${name.value} — iTAB`,
    meta: [{
      name: 'description',
      content: seoData.value?.description ?? `${name.value} — iTAB`
    }]
  }
})

onMounted((): void => {
  if (status.value === 'success') { // After SSR.
    trackPageView()
  }
})

watch(
  [page, sort],
  async (): Promise<void> => {
    if (!isFirstDataLoading.value) {
      scrollTo('scroll-anchor', 60)

      await updateQuery()

      await reloadProductsData()
    }
  },
  { deep: true }
)
watch(
  sort,
  (): void => {
    if (isFirstDataLoading.value) {
      return
    }

    changePage(1)
  }
)
watch(
  status,
  (x: AsyncDataRequestStatus): void => {
    if (x === 'success') {
      nextTick(scrollToSavedPosition)

      trackPageView()
    }
  }
)

function changePage (pageNumber: number): void {
  page.value.number = pageNumber
}

function getProductsParams (): any {
  const { number, size } = page.value

  return helpers.getParams([
    { name: 'filter[categoryId]', value: categoryId.value, condition: true },
    { name: 'page[number]', value: number, condition: true },
    { name: 'page[size]', value: size, condition: true },
    { name: 'sort', value: sort.value, condition: sort.value }
  ])
}

function parseQuery (): void {
  const { page: _page, sort: _sort } = route.query
  const { MostPopular } = ProductSortEnum

  page.value.number = _page !== undefined && /^\+?[1-9]\d*$/.test(String(_page)) && _page !== '1' ? Number(_page) : 1
  sort.value = _sort !== undefined && Object.values(ProductSortEnum).includes(String(_sort)) && _sort !== MostPopular
    ? _sort
    : MostPopular
}

async function reloadProductsData (): Promise<void> {
  isProductsDataReloading.value = true

  const response = await $apiHelper.products.getProducts(getProductsParams())
  const { data: productsData } = JsonApiHelper.denormalizeResponse(response)

  if (data.value?.productsResponse) {
    data.value.productsResponse.data = productsData
  }

  isProductsDataReloading.value = false
}

function trackPageView (): void {
  viewCategory(categoryData.value!.id)
}

async function updateQuery (): Promise<void> {
  const pageNumber = page.value.number
  const newQuery: any = {}

  if (pageNumber !== null && pageNumber !== 1) {
    newQuery.page = pageNumber
  }

  if (sort.value !== null && sort.value !== ProductSortEnum.MostPopular) {
    newQuery.sort = sort.value
  }

  // To avoid an error: "Avoided redundant navigation to current location".
  try {
    await router.replace({ query: newQuery })
  } catch {}
}
</script>

<template>
  <div>
    <section v-if="status === 'success'">
      <div class="container">
        <Breadcrumbs :current="name" :items="breadcrumbsItems" />
      </div>
    </section>

    <Preloader v-if="status === 'pending'" />
    <template v-else-if="status === 'success'">
      <section>
        <div class="container">
          <Heading :badge-text="totalProducts" :title="seoH1" />

          <ThinBanner :banner-image-paths="bannerImagePaths" class="mb-6" :title="name" />

          <HtmlArea v-if="descriptionHtml" class="mb-6" :plain-html="descriptionHtml" />

          <HorizontalMenu v-if="menuItems.length" class="mb-6" :items="menuItems" />
        </div>
      </section>

      <Preloader v-if="isProductsDataReloading" />
      <ProductsWithPagination
        v-else
        v-model:sort="sort"
        :no-products-text="`Нет товаров в категории «${name}».`"
        :page="page"
        :products-data="productsData"
        :total-pages="totalPages"
        @change-page="changePage"
      />
    </template>

    <MindboxRecommendationsSection
      v-if="categoryId"
      class="my-10"
      :data="{ recommendation: { limit: 12, productCategory: { ids: { itab: categoryId } } } }"
      :operation="MindboxOperation.PopularProductsInCategory"
      :title="'Популярное в\xA0категории'"
    />

    <ConsultationsSection class="mt-10" />

    <section class="mt-10">
      <div class="container">
        <HtmlArea
          v-if="status === 'success' && seoBottomDescriptionHtml"
          class="text-sm"
          :plain-html="seoBottomDescriptionHtml"
        />

        <CategoryFaqs v-if="categoryId" :category-id="categoryId" class="mt-10" />
      </div>
    </section>
  </div>
</template>
